/**
 * The Meddyg of Llan-y-Nant (M4-08).
 *
 * The MVP's one deep NPC, and "deep" here means *written* rather than
 * simulated: a schedule you can learn, a voice you can recognise, and three
 * services that matter. She stands in the Meddygon Myddfai tradition — the
 * physicians of Myddfai, whose remedies this game's pharmacopoeia is drawn from
 * — and her register is the one every Welsh grandmother in the world shares:
 * dry, unimpressed, and on your side.
 *
 * Her lines are pools rather than single strings for the reason the wound
 * tables' narration is: a character who says exactly one thing is a vending
 * machine. Two or three is enough — the game picks by tick, so a replay hears
 * the same words in the same order.
 *
 * What she will *not* say is anything about numbers. She never tells you a
 * bleed rate or an infection stage; she tells you the leg is dying. That is the
 * whole of M2-06's rule applied to dialogue.
 */
export const MEDDYG = {
    id: 'meddyg',
    name: 'Nest the Meddyg',
    role: 'the healer',
    /**
     * Her day, and it is deliberately learnable: garden in the morning while the
     * dew is on things, her house through the middle of the day where the light
     * and the fire are, the well at dusk, and home at dark. A player who has been
     * in the valley a week knows where she is without asking, which is what makes
     * a schedule worth having at all.
     */
    schedule: [
        { fromHour: 6, anchor: 'garden' },
        { fromHour: 10, anchor: 'healer' },
        { fromHour: 18, anchor: 'wellside' },
        { fromHour: 20, anchor: 'healer' },
    ],
    placeNames: {
        garden: 'her garden',
        healer: 'the Meddyg’s house',
        wellside: 'the well',
    },
    /**
     * She binds, she lends her cot, and she gossips. She does not trade — what she
     * takes for treating you is payment in kind, not a stall, and a healer who
     * sold spearheads would be a shop with a bandage in it.
     */
    topics: ['greet', 'rumour', 'treat', 'bed'],
    /**
     * What she personally sees: the hurt carried in and the ones she could not
     * keep. She is the second person in the valley who knows somebody is dead,
     * and often the first.
     */
    news: ['hurt', 'death'],
    /**
     * Between a novice and a maxed player, and the number says so: `MEDICINE_DIE_STEP`
     * is 30 and the cap is 60, so 35 puts her one die above somebody who has
     * never bound a wound and one below somebody who has bound fifty. The ceiling
     * belongs to players — the ticket is explicit — and this is where that is
     * actually decided.
     */
    medicine: 35,
    lines: {
        greet: [
            'You are standing in my light.',
            'If you are not bleeding, there is water at the well and I am busy.',
            'Well. You are upright, which is more than some.',
        ],
        treated: [
            'Hold still. — There. Keep it dry and do not pick at it.',
            'That will knit if you let it. Which you will not, but I have said it.',
            'Clean, bound, and better than you deserve. Come back if it goes hot.',
        ],
        unpaid: [
            'My shelves do not fill themselves. Bring me something off the hill.',
            'Yarrow, moss, meat — anything that is not an apology.',
            'I work for the valley, not for nothing. Bring something and I will bind it.',
        ],
        nothingWrong: [
            'There is nothing wrong with you that a day of work would not cure.',
            'You are whole. Go away.',
        ],
        bed: [
            'The cot is by the fire. One of you at a time — the rest can have the floor.',
            'Lie down on it and stay lain down. That is the whole of the treatment.',
        ],
        rumour: [
            'Wolves in the night again. Third time this month.',
            'Somebody has been at the garlic bed, and it was not a rabbit.',
            'They found a fire out on the moor, still warm, and nobody in the valley owned it.',
        ],
        gangrene: [
            'That leg is dying. It must come off, and soon, or you go with it.',
            'It has gone past what a wash will do. I can take it, or you can wait and lose the choice.',
        ],
        badDressing: [
            'Who bound this? A blind man in mittens?',
            'This has been on three days and it smells like it. Off with it.',
        ],
    },
};
/* -------------------------------------------------------------------------- *
 * The rest of Llan-y-Nant (M5-01)
 * -------------------------------------------------------------------------- */
/**
 * Hywel, who keeps the hall (M5-01).
 *
 * He is the rumour system with a beard on it, and that is the whole of his
 * mechanic: he carries every kind of news the valley has, unfiltered, because
 * telling people what happened is his office. What he says is *never* written
 * here — his lines below are only what he says when nothing has happened. The
 * real ones are generated from the event log (`rumours.ts`, `chronicle.ts`), and
 * the gap between the two pools is the ticket's promise made mechanical: the
 * village cannot invent news, so an Elder with nothing to report talks about the
 * weather like anybody else.
 *
 * His day is the hall and the green, and it is short. He is old.
 */
export const ELDER = {
    id: 'elder',
    name: 'Hywel Hen',
    role: 'the elder',
    schedule: [
        { fromHour: 7, anchor: 'hall' },
        // Midday on the green, where he can see the whole of the village at once
        // and everybody coming back off the hill has to walk past him. This is
        // where a player will meet him, and it is the hour to be findable.
        { fromHour: 11, anchor: 'anchor' },
        { fromHour: 15, anchor: 'hall' },
        // By the fire, under whatever M5-05 hangs over it.
        { fromHour: 19, anchor: 'trophy' },
        { fromHour: 22, anchor: 'hall' },
    ],
    placeNames: {
        hall: 'the hall',
        anchor: 'the green',
        trophy: 'the hall fire',
    },
    topics: ['greet', 'rumour'],
    // No `news` filter at all, which is the only such omission in the file:
    // everything the valley did is his to repeat.
    lines: {
        greet: [
            'You are welcome in the hall, whoever you are. Sit if you are sitting.',
            'Well. Another one off the hill with all their limbs. Good.',
            'I know every soul in this valley and I do not know you. Speak up.',
        ],
        rumour: [
            'Nothing. A quiet week, and I have lived long enough to be glad of one.',
            'No news, and that is the news. The fence is standing and everyone is fed.',
            'Ask me tomorrow. Something always happens by tomorrow.',
        ],
    },
};
/**
 * Gwilym at the forge (M5-01) — the economic loop, standing in one place.
 *
 * The claim M4-08 made when it built an NPC framework for exactly one NPC was
 * that the smith would arrive as *data*. He is: everything below is a schedule,
 * a list of topics, some lines and a stall, and there is not one line of code in
 * `@annwn/sim` that knows a smith exists — only a `case 'buy'` that reaches for
 * `def.stall`, which the Meddyg does not have.
 *
 * **The prices are the campaign's pacing.** See `barter.ts` for the values they
 * are quoted in and the arithmetic behind them; the short version is that the
 * whetstone is an afternoon, the spear is a good day, and the byrnie is a group
 * of people deciding together to save for a month. That last one is the only
 * aspirational object in the MVP and it should feel like it.
 */
export const SMITH = {
    id: 'smith',
    name: 'Gwilym the Smith',
    role: 'the smith',
    schedule: [
        // He is at it before anybody else is up and the noise is how the village
        // knows what time it is.
        { fromHour: 5, anchor: 'smithy' },
        { fromHour: 12, anchor: 'anchor' },
        { fromHour: 13, anchor: 'smithy' },
        // Evenings in the hall, which means the stall shuts. A schedule you can
        // learn is only worth learning if being late costs you something.
        { fromHour: 20, anchor: 'hall' },
        { fromHour: 23, anchor: 'smithy' },
    ],
    placeNames: {
        smithy: 'the forge',
        anchor: 'the green',
        hall: 'the hall',
    },
    topics: ['greet', 'rumour', 'trade', 'buy', 'repair'],
    /**
     * What comes through his door: the kills people want made into something, and
     * the wounds they got making them. He does not know what happened at the pen
     * and will not pretend to.
     */
    news: ['kill', 'hurt'],
    /**
     * The stall, and it is short on purpose.
     *
     * Six rungs and one summit. A knife, then a spear, then a seax or an axe
     * depending on whether you have decided you are the one who stands in front —
     * and then the byrnie, which is not a rung, it is the top of the ladder. The
     * whetstone is first in the list because it is the thing a party three days
     * out most wishes it had bought, and the peg-leg is last because it is the
     * thing they most wish they had not needed (M4-05).
     *
     * What is *not* here: a sword, a dane-axe, a shield. Those exist in the
     * catalogue and they belong to what you take off the dead or what M6 gives a
     * character at creation. A village smith in a bad year makes tools and mends
     * edges; a shop that sold the best of everything would flatten the difference
     * between hunting well and shopping well.
     */
    stall: {
        wares: [
            { id: 'whetstone', kind: 'item', price: 8 },
            { id: 'knife', kind: 'weapon', price: 12 },
            { id: 'hide-cap', kind: 'armour', price: 15 },
            { id: 'peg-leg', kind: 'item', price: 20 },
            { id: 'spear', kind: 'weapon', price: 24 },
            { id: 'seax', kind: 'weapon', price: 30 },
            { id: 'axe', kind: 'weapon', price: 45 },
            { id: 'helm', kind: 'armour', price: 70 },
            /**
             * The mail shirt. One of them, and it is priced as a campaign goal
             * (`barter.ts` does the arithmetic): eight to twelve good hunting days of
             * a whole group's pooled yield, saved rather than spent on spears.
             *
             * It is worth that. A byrnie is the difference between a wolf opening you
             * and a wolf bruising you, and a valley where one person has one is a
             * valley that has decided together who stands in front.
             */
            { id: 'byrnie', kind: 'armour', price: 420 },
        ],
        /**
         * Four, and cheap deliberately. `blunted` is M2-04's condition-lite and the
         * fix is a man with a whetstone and a morning — the cost is meant to be
         * *walking home*, not the price. A party that has bought its own whetstone
         * never pays this at all, which is the correct relationship between the two
         * things on the stall.
         */
        repairPrice: 4,
    },
    lines: {
        greet: [
            'Mm. Mind the sparks.',
            'If it is bent, put it on the stone there. If it is you that is bent, that is Nest.',
            'You will have to speak up. I have been at this since five.',
        ],
        rumour: [
            'Nothing worth the breath. Iron is short and it has been short since my father.',
            'Quiet. I have made four spearheads this week and sold none of them.',
        ],
        wares: [
            'This is what there is.',
            'Have a look. No, do not pick it up. Look.',
            'What I have got, and what it costs. I do not haggle.',
        ],
        traded: [
            'Aye. Go on then.',
            'That is fair. Do not bring it back to me bent in a week.',
            'Right. It is yours. Try not to die holding it.',
        ],
        cannotAfford: [
            'Not for that, it is not. Come back with something worth having.',
            'I have a family. Bring me hides, or meat, or anything that is not that.',
            'No. You are short and we both know it.',
        ],
        noSale: [
            'I do not keep that. Nobody in this valley keeps that.',
            'Look at the stall, not at me. If it is not on it, I have not got it.',
        ],
        repaired: [
            'There. That will take a hair off your arm now, so mind where you put it.',
            'Done. An edge is a thing you keep up, not a thing you get back.',
        ],
        nothingToMend: [
            'There is nothing wrong with that. Go and use it.',
            'That does not take an edge and you know it. Away.',
        ],
    },
};
/**
 * The herders: Anwen and Deri (M5-01).
 *
 * Two, and two rather than one for a reason that is entirely about M5-02: when
 * the wolves come over the fence at night, somebody has to be awake to have seen
 * it. Deri's schedule below has him out at the fold at three in the morning —
 * "sleeps badly" written as a stop on a timetable rather than as a trait — and
 * that is the mechanism by which a raid becomes something the village *knows
 * about* rather than something a player finds a carcass from.
 *
 * Their news filter is `raid` and only `raid`. A person who sleeps beside the
 * pen knows what came over the fence; they do not know who was hurt on the moor,
 * and the honest answer to a question they cannot answer is small talk.
 */
export const HERDER_ANWEN = {
    id: 'herder-anwen',
    name: 'Anwen the Herder',
    role: 'a herder',
    schedule: [
        { fromHour: 5, anchor: 'pen' },
        { fromHour: 20, anchor: 'home' },
    ],
    placeNames: { pen: 'the pen', home: 'the herders’ house' },
    topics: ['greet', 'rumour'],
    news: ['raid'],
    lines: {
        greet: [
            'Mind the gate behind you. They will be out in the barley given half a chance.',
            'You are not from the valley. Are you stopping? People do not, much.',
            'That is the cow. Do not stand behind her, she does not like it.',
        ],
        rumour: [
            'All of them accounted for this morning, which is not nothing.',
            'Quiet, thank God. The fence held and they are all where I left them.',
            'Nothing. Grass, rain, sheep. That is the whole of my week.',
        ],
    },
};
export const HERDER_DERI = {
    id: 'herder-deri',
    name: 'Deri the Herder',
    role: 'a herder',
    schedule: [
        // Three in the morning, at the fold, because he does not sleep. This is the
        // stop that makes him M5-02's first witness.
        { fromHour: 3, anchor: 'fold' },
        { fromHour: 21, anchor: 'fireside' },
    ],
    placeNames: { fold: 'the fold', fireside: 'the herders’ fire' },
    topics: ['greet', 'rumour'],
    news: ['raid'],
    lines: {
        greet: ['Aye.', 'Mm.', 'The flock settles before the weather does.'],
        rumour: [
            'Heard nothing. Which does not mean there was nothing.',
            'No. But I was out at three and I will be out at three tomorrow.',
        ],
    },
};
export const VILLAGERS = [MEDDYG, ELDER, SMITH, HERDER_ANWEN, HERDER_DERI];
const VILLAGER_INDEX = new Map(VILLAGERS.map((one) => [one.id, one]));
export const VILLAGER_KIT = VILLAGER_INDEX;
export function villager(id) {
    const found = VILLAGER_INDEX.get(id);
    if (!found)
        throw new Error(`no villager '${id}'`);
    return found;
}
//# sourceMappingURL=villagers.js.map